import {
  HubServerMessageSchema,
  type HubClientMessage,
  type HubServerMessage,
} from '../meta/online/hub';
import type { WebSocketLike } from './live';
import { apiUrl } from './online';

/**
 * The player's hub connection (docs/FRIENDS.md, step 2): one WebSocket while the app is open,
 * reconnecting by itself with a fresh ticket, until the game lets go of it. It moves messages and
 * nothing else: what they mean is the game's business (src/game/hub.ts).
 */

/** How a ticket request went: a ticket (and whose hub), try again later, or stop. */
export type HubTicketAnswer = { ticket: string; player: string } | { retry: true } | { stop: true };

export interface HubSocketOptions {
  ticket: () => Promise<HubTicketAnswer>;
  onMessage: (message: HubServerMessage) => void;
  /** The line opened (again): say hello. */
  onOpen: () => void;
  makeSocket?: (url: string) => WebSocketLike;
}

const OPEN = 1;
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 15000];
/** Pings keep the line up and the online mark fresh (the hub refreshes it once a minute). */
const PING_EVERY_MS = 30_000;

function socketUrl(player: string, ticket: string): string {
  const http = apiUrl(`/hub/${player}/ws?ticket=${encodeURIComponent(ticket)}`);
  const absolute = http.startsWith('/') ? `${location.origin}${http}` : http;
  return absolute.replace(/^http/, 'ws');
}

export class HubSocket {
  private socket: WebSocketLike | null = null;
  private stopped = false;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly options: HubSocketOptions) {
    void this.connect();
  }

  /** Sends if the line is open; false when it is not. */
  send(message: HubClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== OPEN) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  /** Lets go for good. */
  close(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
    this.socket?.close(1000, 'closed');
    this.socket = null;
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    const answer = await this.options.ticket();
    if (this.stopped) return;
    if ('stop' in answer) {
      this.close();
      return;
    }
    if ('retry' in answer) {
      this.later();
      return;
    }
    const make =
      this.options.makeSocket ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
    const socket = make(socketUrl(answer.player, answer.ticket));
    this.socket = socket;
    socket.onopen = () => {
      this.attempts = 0;
      this.options.onOpen();
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(
        () => this.send({ type: 'ping', at: Date.now() }),
        PING_EVERY_MS,
      );
    };
    socket.onmessage = (ev) => this.receive(ev.data);
    socket.onerror = () => {
      // A close follows; reconnecting starts there.
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.later();
    };
  }

  private later(): void {
    if (this.stopped) return;
    const wait = BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)]!;
    this.attempts++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, wait);
  }

  private receive(data: unknown): void {
    if (typeof data !== 'string') return;
    let parsed;
    try {
      parsed = HubServerMessageSchema.safeParse(JSON.parse(data));
    } catch {
      return;
    }
    if (parsed.success && parsed.data.type !== 'pong') this.options.onMessage(parsed.data);
  }
}
